<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';

import { refDebounced } from '@vueuse/core';
import { computed, ref, useId, watch } from 'vue';
import { useRouter } from 'vue-router';

import { getPlatformUserDisplayName, usePlatformOrganizationsQuery, usePlatformUsersQuery } from '@/entities/platform';
import { formatPhoneNumber } from '@/shared/lib';
import { AppInput, BaseModal } from '@/shared/ui';

interface SearchResult {
  hint: string;
  id: string;
  label: string;
  to: RouteLocationRaw;
}

const show = defineModel<boolean>('show', { required: true });

const SEARCH_DEBOUNCE_MS = 250;
const LIMIT = 5;

const router = useRouter();
const listId = `platform-search-${useId()}`;
const search = ref('');
const debouncedSearch = refDebounced(search, SEARCH_DEBOUNCE_MS);
const term = computed(() => debouncedSearch.value.trim());
const enabled = computed(() => show.value && term.value !== '');
const activeIndex = ref(0);

const { result: organizationsResult } = usePlatformOrganizationsQuery(
  () => ({ search: term.value, first: LIMIT, after: null }),
  enabled,
);
const { result: usersResult } = usePlatformUsersQuery(
  () => ({ search: term.value, first: LIMIT, after: null }),
  enabled,
);

const groups = computed(() => {
  if (!enabled.value) {
    return [];
  }

  const organizations: SearchResult[] = (organizationsResult.value?.platformOrganizations.edges ?? []).map(
    ({ node }) => ({
      id: `organization:${node.organization.id}`,
      label: node.organization.title,
      hint: node.organization.domain ?? '',
      to: { name: 'platform.organizations.show', params: { id: node.organization.id } },
    }),
  );
  const people: SearchResult[] = (usersResult.value?.platformUsers.edges ?? []).map(({ node }) => ({
    id: `user:${node.user.id}`,
    label: getPlatformUserDisplayName(node),
    hint: formatPhoneNumber(node.user.phone),
    to: { name: 'platform.users.show', params: { id: node.user.id } },
  }));

  return [
    { id: 'organizations', label: 'Организации', items: organizations },
    { id: 'people', label: 'Пользователи', items: people },
  ].filter((group) => group.items.length > 0);
});
const results = computed(() => groups.value.flatMap((group) => group.items));

watch(results, () => {
  activeIndex.value = 0;
});

watch(show, (open) => {
  if (!open) {
    search.value = '';
  }
});

const open = (item: SearchResult | undefined): void => {
  if (!item) {
    return;
  }

  show.value = false;
  void router.push(item.to);
};

const handleKeydown = (event: KeyboardEvent): void => {
  const count = results.value.length;

  if (event.key === 'ArrowDown' && count > 0) {
    event.preventDefault();
    activeIndex.value = (activeIndex.value + 1) % count;
  } else if (event.key === 'ArrowUp' && count > 0) {
    event.preventDefault();
    activeIndex.value = (activeIndex.value - 1 + count) % count;
  } else if (event.key === 'Enter') {
    event.preventDefault();
    open(results.value[activeIndex.value]);
  }
};
</script>

<template>
  <BaseModal
    v-model:show="show"
    position="top"
    close-on-backdrop
  >
    <template #title>Поиск</template>

    <div class="flex flex-col gap-4">
      <AppInput
        v-model="search"
        autofocus
        placeholder="Телефон, имя, организация или ID"
        @keydown="handleKeydown"
      />

      <p
        v-if="term !== '' && results.length === 0"
        class="text-sm text-(--text-secondary)"
      >
        Ничего не нашлось
      </p>

      <div
        v-else-if="results.length > 0"
        :id="listId"
        role="listbox"
        aria-label="Результаты поиска"
        class="flex flex-col gap-3"
      >
        <div
          v-for="group in groups"
          :key="group.id"
          role="group"
          :aria-label="group.label"
          class="flex flex-col gap-1"
        >
          <p class="px-2 text-xs font-medium text-(--text-secondary)">{{ group.label }}</p>
          <button
            v-for="item in group.items"
            :key="item.id"
            type="button"
            role="option"
            :aria-selected="results[activeIndex]?.id === item.id"
            class="flex min-h-11 items-center justify-between gap-3 rounded-lg px-2 text-left text-sm"
            :class="results[activeIndex]?.id === item.id ? 'bg-(--surface-hover)' : 'hover:bg-(--surface-hover)'"
            @click="open(item)"
            @mousemove="activeIndex = results.indexOf(item)"
          >
            <span class="truncate font-medium">{{ item.label }}</span>
            <span class="shrink-0 text-xs text-(--text-secondary)">{{ item.hint }}</span>
          </button>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
